import React, { useState, useEffect } from 'react';
import { useSession } from '../Utils/SessionContext';
import { API_ENDPOINTS } from '../Utils/ApiConfig';
import { useLocation, useNavigate } from 'react-router-dom';
import EvalDetails from './EvalDetails';
import CreateEvaluation from './CreateEvaluation';
import CreateEvaluationWizard from '../CreateEvaluationComponents/CreateEvaluationWizard';
import AssignEvaluation from './AssignEvaluation';
import SectionHeader from '../Pages/Components/SectionHeader';
import EvalSelectBox from './Shards/EvalSelectBox';

/**
 * Evals Component
 * Reusable component for displaying evaluations for both talents and candidates
 */
const Evals = ({ 
  entity, 
  contextType = 'talent' // 'talent' or 'candidate'
}) => {
  // State for evals data
  const [evals, setEvals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for selected eval and panel visibility
  const [selectedEval, setSelectedEval] = useState(null);
  const [leftPanelVisible, setLeftPanelVisible] = useState(true);
  
  // State for popups
  const [showCreateEvalPopup, setShowCreateEvalPopup] = useState(false);
  const [showAssignEvalPopup, setShowAssignEvalPopup] = useState(false);
  const [showCreateEvaluationWizard, setShowCreateEvaluationWizard] = useState(false);
  
  // Hooks for session and navigation
  const { user, logout } = useSession();
  const location = useLocation();
  const navigate = useNavigate();

  // Function to fetch evals from the API
  const fetchEvals = async () => {
    const entityId = contextType === 'talent' ? entity?.talent_id : entity?.candidate_id;
    const paramName = contextType === 'talent' ? 'tid' : 'cid';
    const apiEndpoint = contextType === 'talent' ? API_ENDPOINTS.GET_TALENT_EVALS : API_ENDPOINTS.GET_CANDIDATE_EVALS;
    
    if (!entityId) {
      setLoading(false);
      setError(`${contextType === 'talent' ? 'Talent' : 'Candidate'} ID is missing`);
      return;
    }

    try {
      setLoading(true);
      
      // Check if user exists
      if (!user) {
        throw new Error('User session not available. Please log in again.');
      }
      
      // Make GET request to the evals endpoint with entity ID as a parameter
      const url = new URL(apiEndpoint);
      url.searchParams.append(paramName, entityId);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${user.access_token}`
        }
      });
      
      // Check if the response is ok
      if (!response.ok) {
        const error = new Error(`Error: ${response.status} ${response.statusText}`);
        error.status = response.status;
        throw error;
      }
      
      // Parse the response data with error handling for empty responses
      let data = [];
      try {
        data = await response.json();
        console.log(`${contextType} evals data:`, data);
      } catch (parseError) {
        console.log('Empty or invalid response received for evals');
        data = [];
      }
      
      // Update evals state
      setEvals(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      console.error(`Error fetching ${contextType} evals:`, err);
      console.log('Error status code:', err.status);
      
      // If it's a 401 error, it's an expired session - log out and redirect to login
      if (err.status === 401) {
        console.log('Session expired or unauthorized. Redirecting to login page.');
        logout();
        navigate('/login');
        return;
      }
      
      setError(err.message || 'Failed to fetch evaluations');
    } finally {
      setLoading(false);
    }
  };

  // Handle eval selection
  const handleEvalSelect = (evalItem) => {
    setSelectedEval(evalItem);
  };

  // Function to handle create evaluation submission 
  const handleCreateEvaluation = async (data) => {
    console.log('Evaluation created:', data);
    
    // Refresh the evals list
    fetchEvals();

    // Show success message
    alert(`Evaluation "${data.evaluationName}" is being generated. It should be ready in approximately 10 minutes - please come back then.`);
  };

  // Render action buttons based on context type
  const renderActionButtons = () => {
    // TALENT
    if (contextType === 'talent') {
      return (
        <>
          <div className="flex gap-2">
            <button
              onClick={() => setShowCreateEvaluationWizard(true)}
              className="px-3 py-2 bg-blue-500 text-white font-semibold rounded hover:bg-blue-600 transition-colors flex items-center"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
              </svg>
              Create New Evaluation
            </button>
            {/* <button
              onClick={() => setShowCreateEvalPopup(true)}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg shadow-sm hover:bg-blue-700 transition-colors whitespace-nowrap"
            >
              Create New Evaluation
            </button> */}
          </div>
          <p className="mt-2 text-sm text-center">
            <span className="text-gray-600">or </span>
            <span 
              onClick={() => setShowAssignEvalPopup(true)}
              className="text-blue-600 hover:text-blue-700 cursor-pointer transition-colors"
            >
              Assign Template Evaluation
            </span>
          </p>
        </>
      );
    // CANDIDATE
    } else {
      return (
        <>
          <button
            onClick={() => setShowAssignEvalPopup(true)}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg shadow-sm hover:bg-blue-700 transition-colors whitespace-nowrap"
          >
            Assign Evaluation (From Job)
          </button>
          <p className="mt-2 text-sm text-center">
            <span className="text-gray-600">or </span>
            <span
              onClick={() => setShowCreateEvaluationWizard(true)}
              className="text-blue-600 hover:text-blue-700 cursor-pointer transition-colors"
            >
              Create Custom Evaluation
            </span>
            {/* <span 
              onClick={() => setShowCreateEvalPopup(true)}
              className="text-blue-600 hover:text-blue-700 cursor-pointer transition-colors"
            >
              Create Custom Evaluation
            </span> */}
          </p>
        </>
      );
    }
  };
  


  // Fetch evals when component mounts
  useEffect(() => {
    fetchEvals();
  }, []);

  // Set the first eval as selected when evals are loaded
  useEffect(() => {
    if (!loading && !error && evals.length > 0 && !selectedEval) {
      handleEvalSelect(evals[0]);
    }
  }, [loading, error, evals, selectedEval]);

  return (
    <div className="space-y-4">
      {/* Header section - always visible */}
      <SectionHeader
        title={contextType === 'talent' ? 'Assigned Evaluations' : 'Candidate Assigned Evaluations'}
        description={contextType === 'talent' 
          ? 'This tab displays all evaluations assigned to this talent.'
          : 'This tab displays all evaluations assigned to this candidate to complete for this job.'}
        actions={!loading && !error ? [
          <div key="eval-actions" className="flex flex-col gap-2">
            {renderActionButtons()}
          </div>
        ] : []}
      />
      
      {/* Two-column layout */}
      <div className="flex flex-col md:flex-row bg-white shadow-md rounded-lg border border-gray-200 overflow-hidden relative">
        {/* Left column - Evals list */}
        <div className={`w-full md:w-1/4 border-r border-gray-200 bg-gray-50 transition-all duration-300 ${!leftPanelVisible ? 'md:!w-0 md:opacity-0 md:overflow-hidden md:p-0' : ''}`}>
          <div className="p-4 border-b border-gray-200 bg-white flex justify-between items-center">
            <h3 className="font-semibold text-gray-700">Evaluation List</h3>
            {/* Toggle button for left panel */}
            <button 
              className="p-1 rounded-md hover:bg-gray-100 focus:outline-none transition-colors"
              onClick={() => setLeftPanelVisible(!leftPanelVisible)}
              title={leftPanelVisible ? "Hide panel" : "Show panel"}
            >
              <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
            </button>
          </div>
          <div className="p-4">
            {loading && (
              <div className="p-6 flex flex-col items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-3"></div>
                <p className="text-gray-600">Loading evaluations...</p>
              </div>
            )}
            
            {!loading && error && (
              <div className="p-6 flex flex-col items-center justify-center">
                <div className="bg-red-100 text-red-700 p-3 rounded-lg mb-3 w-full">
                  <p className="font-medium text-sm">Error</p>
                  <p className="text-xs mt-1">{error}</p>
                </div>
                <button 
                  onClick={() => fetchEvals()}
                  className="mt-2 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Try Again
                </button>
              </div>
            )}
            
            {!loading && !error && evals.length === 0 && (
              <div className="p-6 flex flex-col items-center justify-center">
                <svg className="w-12 h-12 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <p className="text-gray-500 font-medium mb-2">No Evaluations Found</p>
                <p className="text-gray-400 text-sm text-center">
                  No evaluations are available for this {contextType}.
                </p>
              </div>
            )}
            
            {!loading && !error && evals.length > 0 && (
              <div className="space-y-3">
                {evals.map((evalItem, index) => (
                  <EvalSelectBox
                    key={index}
                    evalItem={evalItem}
                    isSelected={selectedEval && selectedEval.eval_id === evalItem.eval_id}
                    onClick={handleEvalSelect}
                    index={index}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
        
        
        {/* Toggle button that appears when panel is hidden - positioned in the top-left corner of the right column */}
        {!leftPanelVisible && (
          <div className="absolute left-0 top-0 z-1">
            <button 
              className="bg-white py-1 px-2 text-xs rounded-br-md border border-t-0 border-l-0 border-gray-200 shadow-sm hover:bg-gray-50 focus:outline-none flex items-center"
              onClick={() => setLeftPanelVisible(true)}
              title="Show panel"
            >
              <svg className="h-3 w-3 text-gray-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
              </svg>
              <span className="text-gray-600">Show Evaluation List</span>
            </button>
          </div>
        )}
        
        {/* Right column - Eval details */}
        <div className={`w-full p-6 bg-white transition-all duration-300 ${leftPanelVisible ? 'md:w-3/4' : 'md:w-full'}`}>
          <EvalDetails 
            evalId={selectedEval?.eval_id}
            contextType={contextType}
          />
        </div>
      </div>
      
      {/* Create Evaluation Popup */}
      {showCreateEvalPopup && (
        <CreateEvaluation 
          isOpen={showCreateEvalPopup}
          onClose={() => setShowCreateEvalPopup(false)}
          onSubmit={handleCreateEvaluation}
          job={null}
          talent={entity ? (contextType === 'candidate' ? {
            // IF CANDIDATE, the entity is a candidate object and the talent data is in candidate.data (see how its passed in CandidateEvals.js)
            talent_id: entity.talent?.id || entity.talent?.talent_id,
            name: `${entity.talent?.fname || ''} ${entity.talent?.lname || ''}`.trim(),
            email: entity.talent?.email,
            roleCategory: entity.talent?.roleCategory,
            status: entity.talent?.status,
            candidate_id: entity.candidate_id 
          } : {
            // IF TALENT, the entity is a talent object and talent data is directly available (see how its passed in TalentEvals.js)
            talent_id: entity.id || entity.talent_id,
            name: `${entity.fname || ''} ${entity.lname || ''}`.trim(),
            email: entity.email,
            roleCategory: entity.roleCategory,
            status: entity.status
          }) : null}
          contextType={contextType} // Pass through the actual context ('candidate', 'talent', or 'job')
        />
      )}
      
      {/* Assign Evaluation Popup */}
      {showAssignEvalPopup && (
        <AssignEvaluation 
          isOpen={showAssignEvalPopup}
          onClose={() => setShowAssignEvalPopup(false)}
          onAssignSuccess={() => {
            // Clear current selection and refresh the evaluations list
            setSelectedEval(null);
            fetchEvals();
          }}
          contextType={contextType}
          jobId={entity?.job_id}
          candidateId={entity?.candidate_id}
          talentId={entity?.talent_id}
        />
      )}

      {/* Create Evaluation Wizard */}
      {showCreateEvaluationWizard && (
        <CreateEvaluationWizard
          isOpen={showCreateEvaluationWizard}
          onClose={() => setShowCreateEvaluationWizard(false)}
          talentData={entity ? (contextType === 'candidate' ? {
            // IF CANDIDATE, the entity is a candidate object and the talent data is in candidate.data
            id: entity.talent?.id || entity.talent?.talent_id,
            fname: entity.talent?.fname,
            lname: entity.talent?.lname,
            email: entity.talent?.email,
            roleCategory: entity.talent?.roleCategory,
            status: entity.talent?.status,
            candidate_id: entity.candidate_id 
          } : {
            // IF TALENT, the entity is a talent object and talent data is directly available
            id: entity.id || entity.talent_id,
            fname: entity.fname,
            lname: entity.lname,
            email: entity.email,
            roleCategory: entity.roleCategory,
            status: entity.status
          }) : null}
        />
      )}
    </div>
  );
};

export default Evals;
